import React, { useContext, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import BottomDialog from "./BottomDialog.js";
import CenterDialog from "./CenterDialog.js";
import { useDimensions } from "src/utils/useDimensions.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import {
  getCalendarApiUrl,
  getWebcalFormat,
  handleOpenWebcal,
} from "src/utils/Utils.js";
import { useNavigate } from "react-router-dom";
import { getPageRoute } from "src/services/Config.js";
import { finalPulse } from "../ProgressButton.js";
import { UAParser } from "ua-parser-js";

const OpenCalendarDialogContent = ({ handleOpenCalendar, calendar }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        padding: 2,
      }}
    >
      {/* Pagination Dots at the top */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 4 }}>
        <Box
          sx={{
            width: "24px",
            height: "8px",
            borderRadius: "4px",
            backgroundColor: "primary.main",
          }}
        />
        <Box
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "4px",
            backgroundColor: "action.disabled",
          }}
        />
      </Box>

      {/* Current step header with number */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "primary.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            fontWeight: 600,
            mr: 2,
          }}
        >
          1
        </Box>
        <Typography
          variant="title"
          sx={{ fontSize: "26px", textWrap: "pretty" }}
        >
          Open Your Calendar
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Follow the instructions in your calendar app to finish subscribing.
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }}>
        Once you're done, pop back here to confirm so your subscription stays
        active – and if it doesn't go through, we'll help you fix it.
      </Typography>
      <Button
        variant="contained"
        onClick={handleOpenCalendar}
        sx={{
          width: "100%",
          animation: `${finalPulse} 3s infinite`,
          mb: user?.provider === "google" ? 2 : 4,
        }}
      >
        <span>OPEN CALENDAR</span>
      </Button>

      {/* Google Connect option - only shown for Google users */}
      {user?.provider === "google" && (
        <>
          <Button
            variant="outlined"
            onClick={() => {
              customEvent({
                name: "user_click_switch_google_connect",
                category: "SubscribingCalendar",
                label: "Switch to Google Connect Click",
                data: {
                  user_id: user?._id,
                  user_email: user?.email,
                  user_handle: user?.handle ?? "no_handle",
                  calendar_id: calendar?._id,
                },
              });
              navigate(
                getPageRoute("help", "SUBSCRIBING_GOOGLE_CALENDAR_STEPS", {
                  handle: calendar?.handle,
                  calendarId: calendar?._id ?? calendar?.id,
                }),
                { state: { calendar } }
              );
            }}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
            }}
          >
            SWITCH TO GOOGLE CONNECT
          </Button>
          <Typography
            variant="body2"
            sx={{ mb: 4, color: "text.secondary", textAlign: "left" }}
          >
            <strong>Recommended for Google users:</strong> We use Google
            Calendar's API to automatically sync across all your devices,
            providing faster updates and a seamless experience. No manual setup
            required.
          </Typography>
        </>
      )}

      {/* Show upcoming steps below current step content */}
      <Box sx={{ pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          What's next:
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 1.5,
            opacity: 0.6,
          }}
        >
          <Box
            sx={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: "action.disabled",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: 600,
              mr: 2,
            }}
          >
            2
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "14px",
            }}
          >
            Verify and Troubleshoot
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const OpenCalendarDialog = ({ open, handleClose, calendar }) => {
  const { isSmallScreen } = useDimensions();
  const { user } = useContext(AuthContext);
  const calendarApiUrl = getCalendarApiUrl({ calendar, user });
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      customEvent({
        name: "landed_on_open_calendar_dialog",
        category: "SubscribingCalendar",
        label: "Landed on Open Calendar Dialog",
        data: {
          user_id: user?._id,
          user_email: user?.email,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenCalendar = () => {
    customEvent({
      name: `user_click_open_calendar`,
      category: "SubscribingCalendar",
      label: "Open Calendar Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    customEvent({
      name: `click_open_calendar_${user.provider}`,
      category: "SubscribingCalendar",
      label: "Open Calendar Click",
      data: {
        user_id: user._id,
        user_email: user.email,
        user_handle: user.handle ?? "no_handle",
      },
    });

    const uaParser = new UAParser();
    const parserResults = uaParser.getResult();
    const isIOS = parserResults.os.name === "iOS";
    // For Google users without iOS, open the Google Calendar add by URL page, otherwise use webcal (calendar screen/app)
    if (user?.provider === "google" && !isIOS) {
      window.open(
        "https://calendar.google.com/calendar/u/0/r/settings/addbyurl",
        "_blank"
      );
    } else {
      handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);
    }

    navigate(
      getPageRoute(
        "help",
        "SUBSCRIBING_CALENDAR_VERIFY",
        { handle: calendar.handle, calendarId: calendar._id },
        { email: user.email, is_reminder: false }
      ),
      {
        state: { calendar },
      }
    );
  };

  return (
    <>
      {isSmallScreen ? (
        <BottomDialog
          open={open}
          handleClose={handleClose}
          hideCloseButton
          hideRectangle
          bubbleStyle
        >
          <OpenCalendarDialogContent
            handleOpenCalendar={handleOpenCalendar}
            calendar={calendar}
          />
        </BottomDialog>
      ) : (
        <CenterDialog
          open={open}
          handleClose={handleClose}
          sx={{ height: "auto", ".MuiPaper-root": { maxWidth: "393px" } }}
          hideCloseButton
        >
          <OpenCalendarDialogContent
            handleOpenCalendar={handleOpenCalendar}
            calendar={calendar}
          />
        </CenterDialog>
      )}
    </>
  );
};

export default OpenCalendarDialog;
